import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Shield, Key, AlertCircle, CheckCircle, Calendar } from 'lucide-react';
import { database, ref, get, set } from '../../firebase';

const ChangePassword = () => {
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentDate, setCurrentDate] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('At least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('At least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('At least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Basic validation
    if (!passwords.oldPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setError('All password fields are required');
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Advanced password validation
    const passwordErrors = validatePassword(passwords.newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password must contain: ${passwordErrors.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      // Get current date for the date-based structure (YYYY-MM-DD format)
      const dateKey = new Date().toISOString().split('T')[0];
      setCurrentDate(dateKey);
      
      // First try to find adminAccount in the date-based structure
      const dateRef = ref(database, `admin/${dateKey}/adminAccount`);
      const dateSnapshot = await get(dateRef);
      
      let adminData = null;
      let adminPath = '';
      
      if (dateSnapshot.exists()) {
        // Found in date-based structure
        adminData = dateSnapshot.val();
        adminPath = `admin/${dateKey}/adminAccount`;
        console.log('✅ Found admin in date-based structure:', adminPath);
      } else {
        // If not found in date structure, check root admin as fallback
        const rootAdminRef = ref(database, 'admin/adminAccount');
        const rootSnapshot = await get(rootAdminRef);
        
        if (rootSnapshot.exists()) {
          adminData = rootSnapshot.val();
          adminPath = 'admin/adminAccount';
          console.log('⚠️ Found admin in root, will migrate to date structure');
          
          // Migrate to date-based structure
          const migratedData = {
            ...adminData,
            migratedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          await set(ref(database, `admin/${dateKey}/adminAccount`), migratedData);
          adminPath = `admin/${dateKey}/adminAccount`;
          adminData = migratedData;
        } else {
          throw new Error('Admin account not found');
        }
      }

      // Check old password
      if (!adminData || adminData.password !== passwords.oldPassword) {
        throw new Error('Current password is incorrect');
      }

      // Update password in the correct structure
      const updatedAdmin = {
        ...adminData,
        password: passwords.newPassword,
        updatedAt: new Date().toISOString(),
        passwordChangedAt: new Date().toISOString(),
        lastPasswordChange: new Date().toISOString()
      };

      // Save to the correct path (date-based structure)
      await set(ref(database, adminPath), updatedAdmin);

      setSuccess(`✅ Admin password updated successfully in ${adminPath}!`);
      setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
      
      // Also update in logs if exists
      try {
        const logsRef = ref(database, `admin/${dateKey}/logs`);
        const logsSnapshot = await get(logsRef);
        
        if (logsSnapshot.exists()) {
          const logs = logsSnapshot.val() || [];
          const newLog = {
            type: 'password_change',
            userId: 'admin',
            timestamp: new Date().toISOString(),
            ipAddress: 'Admin Dashboard',
            status: 'success',
            message: 'Admin password changed successfully'
          };
          
          await set(ref(database, `admin/${dateKey}/logs`), [...logs, newLog]);
        }
      } catch (logError) {
        console.log('Log update optional:', logError);
      }
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordErrors = validatePassword(passwords.newPassword);
  const isPasswordValid = passwords.newPassword && passwordErrors.length === 0;

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2 font-heading">Change Admin Password</h1>
        <p className="text-silver font-body">Secure your administrator account with a new password</p>
      </div>

      <div className="card-glow p-8">
        <div className="flex items-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mr-4">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground font-heading">Secure Admin Account</h2>
            <p className="text-silver mt-1 font-body">Update administrator password for enhanced security</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-600/30 rounded-xl text-red-300 flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
            <span className="font-body">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-900/30 border border-green-600/30 rounded-xl text-green-300 flex items-start">
            <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-body">{success}</span>
              {currentDate && (
                <div className="mt-2 text-sm text-green-400 font-body flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Saved in: admin/{currentDate}/adminAccount
                </div>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Current Password */}
          <div>
            <label className="block text-silver mb-3 font-heading">
              <Key className="inline h-4 w-4 mr-2" />
              Current Admin Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.old ? "text" : "password"}
                name="oldPassword"
                value={passwords.oldPassword}
                onChange={handleChange}
                placeholder="Enter current admin password"
                className="w-full pl-12 pr-12 py-4 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:border-primary transition-colors font-body"
                required
              />
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-silver" />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('old')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-silver hover:text-foreground transition-colors"
              >
                {showPasswords.old ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* New Password with Validation */}
          <div>
            <label className="block text-silver mb-3 font-heading">
              <Key className="inline h-4 w-4 mr-2" />
              New Admin Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? "text" : "password"}
                name="newPassword"
                value={passwords.newPassword}
                onChange={handleChange}
                placeholder="Enter new admin password"
                className={`w-full pl-12 pr-12 py-4 bg-secondary border rounded-xl text-foreground focus:outline-none transition-colors font-body ${
                  passwords.newPassword 
                    ? isPasswordValid 
                      ? 'border-green-500/50 focus:border-green-500' 
                      : 'border-red-500/50 focus:border-red-500'
                    : 'border-border focus:border-primary'
                }`}
                required
              />
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-silver" />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-silver hover:text-foreground transition-colors"
              >
                {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            {/* Password Validation Rules */}
            {passwords.newPassword && (
              <div className="mt-4 p-4 bg-secondary/50 rounded-lg border border-border">
                <div className="text-sm font-medium text-silver mb-2 font-heading">Password Requirements:</div>
                <div className="space-y-2">
                  {[
                    { rule: 'At least 8 characters', valid: passwords.newPassword.length >= 8 },
                    { rule: 'At least one uppercase letter (A-Z)', valid: /[A-Z]/.test(passwords.newPassword) },
                    { rule: 'At least one lowercase letter (a-z)', valid: /[a-z]/.test(passwords.newPassword) },
                    { rule: 'At least one number (0-9)', valid: /[0-9]/.test(passwords.newPassword) },
                    { rule: 'At least one special character (!@#$%^&*)', valid: /[!@#$%^&*(),.?":{}|<>]/.test(passwords.newPassword) }
                  ].map((req, index) => (
                    <div key={index} className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-3 ${req.valid ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={`text-sm ${req.valid ? 'text-green-400' : 'text-red-400'} font-body`}>
                        {req.rule}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-silver mb-3 font-heading">
              <Key className="inline h-4 w-4 mr-2" />
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new admin password"
                className={`w-full pl-12 pr-12 py-4 bg-secondary border rounded-xl text-foreground focus:outline-none transition-colors font-body ${
                  passwords.confirmPassword 
                    ? passwords.newPassword === passwords.confirmPassword
                      ? 'border-green-500/50 focus:border-green-500'
                      : 'border-red-500/50 focus:border-red-500'
                    : 'border-border focus:border-primary'
                }`}
                required
              />
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-silver" />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-silver hover:text-foreground transition-colors"
              >
                {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {passwords.confirmPassword && passwords.newPassword === passwords.confirmPassword && (
              <div className="mt-2 text-sm text-green-400 font-body flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Passwords match
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isPasswordValid || !passwords.confirmPassword || passwords.newPassword !== passwords.confirmPassword}
            className="btn-electric w-full py-4 text-lg font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-heading"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                Updating Admin Password...
              </div>
            ) : (
              'Update Admin Password'
            )}
          </button>
        </form>

        <div className="mt-12 pt-8 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-5 bg-secondary/30 rounded-xl border border-border">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground font-heading">Security Tips</h3>
              </div>
              <ul className="space-y-2">
                <li className="text-sm text-silver flex items-start font-body">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mr-2 mt-1.5"></div>
                  Use a unique password not used elsewhere
                </li>
                <li className="text-sm text-silver flex items-start font-body">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mr-2 mt-1.5"></div>
                  Avoid personal information in passwords
                </li>
                <li className="text-sm text-silver flex items-start font-body">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mr-2 mt-1.5"></div>
                  Consider using a password manager
                </li>
              </ul>
            </div>

            <div className="p-5 bg-secondary/30 rounded-xl border border-border">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground font-heading">Storage Location</h3>
              </div>
              <p className="text-sm text-silver font-body">
                Password changes are saved in the date-based structure: 
                <code className="block mt-2 bg-secondary p-2 rounded text-foreground font-mono text-xs">
                  admin/YYYY-MM-DD/adminAccount
                </code>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;